import { FC, useMemo } from 'react';

import { useTranslate } from 'hooks/useTranslate';
import {
  formatCurrencyDecimals,
  formatCurrencyDecimalsLong,
  formatNumber,
} from 'utils/formats';

import {
  IncentiveOffer,
  IncentiveOfferType,
} from 'modules/incentivesMarket/types';
import { IncentivesMarketPlanContext } from 'modules/incentivesMarket/types/IncentivesMarketBeneficiaryContext';
import IconTooltip from 'modules/_shared/components/Icon/IconTooltip';
import { calculateSellerLimitsPlanBreakdown } from 'modules/incentivesMarket/utils/calculateSellerSellLimits';

type IncentiveOfferSellerLimitsBannerProps = {
  offer: IncentiveOffer;
  maxTotalSellAmount?: number;
  maxSellableUnits?: number;
  sellerPlans?: IncentivesMarketPlanContext[];
};

const IncentiveOfferSellerLimitsBanner: FC<
  IncentiveOfferSellerLimitsBannerProps
> = ({ offer, maxTotalSellAmount, maxSellableUnits, sellerPlans = [] }) => {
  const { t } = useTranslate();
  const limits = offer.sellerParticipationLimits;

  const planBreakdown = useMemo(
    () =>
      calculateSellerLimitsPlanBreakdown({
        sellerParticipationLimits: limits,
        plans: sellerPlans,
        unitPrice: offer.unitPrice,
      }),
    [limits, sellerPlans, offer.unitPrice]
  );

  if (
    offer.type !== IncentiveOfferType.BUY ||
    !limits ||
    limits.allowSellAll !== false
  ) {
    return null;
  }

  const rules: string[] = [];

  if (
    limits.maxPercentUnvested !== undefined &&
    limits.maxPercentUnvested < 100
  ) {
    rules.push(
      t('IncentivesMarketSellerLimitMaxPercentAvailable', {
        percent: formatNumber(limits.maxPercentUnvested),
      })
    );
  }

  if (limits.maxAmount && limits.maxAmount > 0) {
    rules.push(
      t('IncentivesMarketSellerLimitMaxAmount', {
        amount: formatCurrencyDecimals(limits.maxAmount),
      })
    );
  }

  if (limits.maxStocks && limits.maxStocks > 0) {
    rules.push(
      t('IncentivesMarketSellerLimitMaxStockUnits', {
        units: formatNumber(limits.maxStocks),
      })
    );
  }

  if (limits.maxPercentStocks && limits.maxPercentStocks > 0) {
    rules.push(
      t('IncentivesMarketSellerLimitMaxStockPercent', {
        percent: formatNumber(limits.maxPercentStocks),
      })
    );
  }

  if (limits.maxPhantoms && limits.maxPhantoms > 0) {
    rules.push(
      t('IncentivesMarketSellerLimitMaxPhantomUnits', {
        units: formatNumber(limits.maxPhantoms),
      })
    );
  }

  if (limits.maxPercentPhantoms && limits.maxPercentPhantoms > 0) {
    rules.push(
      t('IncentivesMarketSellerLimitMaxPhantomPercent', {
        percent: formatNumber(limits.maxPercentPhantoms),
      })
    );
  }

  if (!rules.length) {
    rules.push(t('IncentivesMarketSellerLimitsCustom'));
  }

  const showMaxSellSummary =
    maxTotalSellAmount !== undefined && maxSellableUnits !== undefined;

  return (
    <div className="alert alert-pro alert-info mb-4">
      <div className="alert-text">
        <h6 className="alert-heading fs-13px mb-2">
          {t('IncentivesMarketSellerLimitsBannerTitle')}
        </h6>
        <ul className="list list-sm mb-0">
          {rules.map((rule) => (
            <li key={rule} className="list-item pb-1 fs-13px">
              {rule}
            </li>
          ))}
        </ul>

        {/* {showMaxSellSummary && (
          <div className="mb-0 mt-2 fs-13px">
            <p className="mb-1">
              {t('IncentivesMarketSellerLimitMaxTotalSellAmount', {
                amount: formatCurrencyDecimals(maxTotalSellAmount),
              })}
            </p>
            <p className="mb-0">
              {t('IncentivesMarketSellerLimitMaxTotalSellAmountUnits', {
                units: formatNumber(maxSellableUnits),
              })}
            </p>
          </div>
        )} */}

        {planBreakdown.length > 0 && (
          <div className="alert alert-info mt-3 mb-0 py-2 px-3">
            <div className="d-flex align-items-center mb-2">
              <h6 className="alert-heading fs-12px mb-0">
                {t('IncentivesMarketSellerLimitPlanBreakdownTitle')}
              </h6>
              <IconTooltip
                id="incentive-offer-seller-limits-plan-breakdown-fifo-tooltip"
                tooltip={t('IncentivesMarketSellUnitsFifoTooltip')}
                place="top"
                className="text-soft z-9999"
                wrapperClassName="d-inline ml-1"
              />
            </div>
            <ul className="list list-sm mb-0">
              {planBreakdown.map((row) => {
                const rowKey = row.planId || row.beneficiaryId;
                const unitProfitPrice = Math.max(
                  0,
                  offer.unitPrice - row.sharePrice
                );

                return (
                  <li key={rowKey} className="list-item pb-1 fs-12px">
                    {t('IncentivesMarketSellerLimitPlanBreakdownRow', {
                      planName: row.planName,
                      units: formatNumber(row.cappedUnits),
                      unitProfitPrice:
                        formatCurrencyDecimalsLong(unitProfitPrice),
                      offerUnitPrice: formatCurrencyDecimalsLong(
                        offer.unitPrice
                      ),
                      sharePrice: formatCurrencyDecimalsLong(row.sharePrice),
                      maxAmount: formatCurrencyDecimals(row.maxPlanAmount),
                    })}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default IncentiveOfferSellerLimitsBanner;
